<template>
  <div class="p-3">
    <!-- Info Panel -->
    <div class="bg-blue-50 mb-5 animate-fade-in">
      <div class="flex items-start">
        <h4>Create Scenario</h4>
        <div class="p-3 scenario-info">
          <p class="text-sm text-blue-800 mb-1" style="font-weight:500; color:dodgerblue;">
            <md-icon class="text-primary mr-1" style="font-size: 18px">info</md-icon> About Scenario Details
          </p>
          <div class="ml-4 text-blue-700">
            <p class="mb-0" style="color:cornflowerblue;">
              Create your healthcare training scenario by selecting the skillmap, step, and customizing the environment, 
              healthcare professional, and patient details. These choices will guide the AI image generation.
            </p>
          </div>
        </div>
      </div>
    </div>

    <b-form class="" @submit.prevent="goToNextTab">
      <!-- Skillmap & Step Section -->
      <div class="scenario-section mb-4">
        <div class="section-header mb-3">
          <h5 class="text-lg font-medium text-gray-900">Skillmap & Step</h5>
        </div>
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div class="row">
            <b-form-group label="Skillmap" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select skillmap"
                id="skillmap"
                v-model="skillmap.id"
                :options="schoolSkillMaps"
                :reduce="opt => opt.id"
                :loading="isLoadingSkillmaps"
                :clearable="false"
                @input="onSkillCardChange((name = 'skillmap'))"
              />
              <b-form-invalid-feedback :state="!skillmapRequired">This field is required.</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Step" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select skill step"
                id="skill-step"
                :disabled="!skillmap.id"
                v-model="skillmap.skillStep"
                :options="skillStepOptions"
                :reduce="opt => opt.id"
                :loading="isLoadingSkillSteps"
                :clearable="false"
                @input="onSkillCardChange()"
              />
              <b-form-invalid-feedback :state="!skillstepRequired">This field is required.</b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Healthcare Professional Section -->
      <div class="scenario-section mb-4">
        <div class="section-header mb-3">
          <h5 class="text-lg font-medium text-gray-900">Healthcare Professional</h5>
        </div>
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div class="row mb-3">
            <b-form-group label="Race/Ethnicity" label-class="form-label" class="col-md-4">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select race"
                v-model="skillmap.race"
                :options="raceOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Gender" label-class="form-label" class="col-md-4">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select gender"
                v-model="skillmap.gender"
                :options="genderOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Age Range" label-class="form-label" class="col-md-4">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select age range"
                v-model="skillmap.actorAge"
                :options="actorAgeOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
          </div>
          <div class="row">
            <b-form-group label="Uniform Type" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select uniform type"
                v-model="skillmap.uniformType"
                :options="uniformTypeOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Uniform Color" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select uniform color"
                v-model="skillmap.uniformColor"
                :options="uniformColorOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Patient Section (Conditional) -->
      <div v-if="requirePatient" class="scenario-section mb-4">
        <div class="section-header mb-3">
          <h5 class="text-lg font-medium text-gray-900">Patient Details</h5>
        </div>
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div class="row mb-3">
            <b-form-group label="Age Group" label-class="form-label" class="col-md-4">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select patient age"
                v-model="skillmap.patientAge"
                :options="patientAgeOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Condition" label-class="form-label" class="col-md-4">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select patient condition"
                v-model="skillmap.patientCondition"
                :options="patientConditionOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Position" label-class="form-label" class="col-md-4">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select patient position"
                v-model="skillmap.patientPosition"
                :options="patientPositionOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
          </div>
          <div class="row">
            <b-form-group label="Race/Ethnicity" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select patient race"
                v-model="skillmap.patientRace"
                :options="raceOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Gender" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select patient gender"
                v-model="skillmap.patientGender"
                :options="genderOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Environment Section -->
      <div class="scenario-section mb-4">
        <div class="section-header mb-3">
          <h5 class="text-lg font-medium text-gray-900">Environment & Scene</h5>
        </div>
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div class="row mb-3">
            <b-form-group label="Setting" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select environment setting"
                v-model="skillmap.envSetting"
                :options="environmentSettingOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Room Type" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select room type"
                v-model="skillmap.roomType"
                :options="roomTypeOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
          </div>
          <div class="row">
            <b-form-group label="Time of Day" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select time of day"
                v-model="skillmap.timeDay"
                :options="timeOfDayOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
            <b-form-group label="Scene Mood" label-class="form-label" class="col-md-6">
              <v-select
                class="form-control v-select-custom"
                placeholder="Select scene mood"
                v-model="skillmap.sceneMood"
                :options="sceneMoodOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                @input="onSkillCardChange()"
              />
            </b-form-group>
          </div>
        </div>
      </div>

      <!-- Navigation Button -->
      <div class="mt-6 d-flex justify-content-end">
        <b-btn variant="primary" type="submit">
          Next: Competition
          <i class="material-icons ml-1">navigate_next</i>
        </b-btn>
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ScenarioDetails',
  components: {},
  props: {
    onNext: {
      type: Function,
      required: true,
    },

    // formData: {
    //   type: Object,
    //   required: true,
    // },
  },
  data() {
    return {
      skillmap: {
        id: '',
        skillStep: '',
        race: 'black',
        gender: 'woman',
        actorAge: 'young_adult',
        uniformType: 'scrubs',
        uniformColor: 'blue',
        patientAge: 'pediatric',
        patientCondition: 'calm',
        patientPosition: 'lying',
        patientRace: 'black',
        patientGender: 'woman',
        envSetting: 'hospital',
        roomType: 'patient_room',
        timeDay: 'morning',
        sceneMood: 'calm',
      },
      requirePatient: true,
      skillmapRequired: false,
      skillstepRequired: false,
      schoolSkillMaps: [],
      isLoadingSkillmaps: false,
      skillStepOptions: [],
      isLoadingSkillSteps: false,
      skillMaps: [],
      raceOptions: [
        { label: 'Black', value: 'black' },
        { label: 'White', value: 'white' },
        { label: 'Hispanic', value: 'hispanic' },
        { label: 'Asian', value: 'asian' },
        { label: 'Multiracial', value: 'multiracial' },
        { label: 'Any', value: 'any_actor_race' },
      ],
      genderOptions: [
        { label: 'Woman', value: 'woman' },
        { label: 'Man', value: 'man' },
        { label: 'Transgender', value: 'transgender' },
        { label: 'Nonbinary', value: 'nonbinary' },
        { label: 'Any', value: 'any_actor_gender' },
      ],
      actorAgeOptions: [
        { label: 'Young Adult (20s–30s)', value: 'young_adult' },
        { label: 'Middle-Aged (40s–50s)', value: 'middle_aged' },
        { label: 'Older Adult (60+)', value: 'older_adult' },
        { label: 'Any', value: 'any_actor_age' },
      ],
      uniformTypeOptions: [
        { label: 'Standard Scrubs', value: 'scrubs' },
        { label: 'Scrubs with PPE (Gown, Gloves, Mask)', value: 'scrubs_ppe' },
        { label: 'Any', value: 'any_uniform_type' },
      ],
      uniformColorOptions: [
        { label: 'Blue', value: 'blue' },
        { label: 'Maroon', value: 'maroon' },
        { label: 'Green', value: 'green' },
        { label: 'Navy', value: 'navy' },
        { label: 'Gray', value: 'gray' },
        { label: 'Any', value: 'any_uniform_color' },
      ],
      patientAgeOptions: [
        { label: 'Pediatric', value: 'pediatric' },
        { label: 'Adult', value: 'adult' },
        { label: 'Older Adult', value: 'older_adult' },
        { label: 'Any', value: 'any_patient_age' },
      ],
      patientConditionOptions: [
        { label: 'Calm', value: 'calm' },
        { label: 'Confused', value: 'confused' },
        { label: 'Anxious', value: 'anxious' },
        { label: 'Agitated', value: 'agitated' },
        { label: 'Nonverbal', value: 'nonverbal' },
        { label: 'Sleeping', value: 'sleeping' },
        { label: 'Post-Surgery', value: 'post_surgery' },
        { label: 'Bedbound', value: 'bedbound' },
        { label: 'Dementia', value: 'dementia' },
        { label: 'Any', value: 'any_patient_condition' },
      ],
      patientPositionOptions: [
        { label: 'Lying in Bed', value: 'lying' },
        { label: 'Sitting in Wheelchair', value: 'sitting' },
        { label: 'Standing', value: 'standing' },
        { label: 'Being Transferred', value: 'transferring' },
        { label: 'Using Bedpan', value: 'bedpan' },
        { label: 'Side-Lying Position', value: 'side_lying' },
        { label: 'Any', value: 'any_patient_position' },
      ],
      environmentSettingOptions: [
        { label: 'Hospital', value: 'hospital' },
        { label: 'Emergency Room', value: 'emergency_room' },
        { label: 'Nursing Home', value: 'nursing_home' },
        { label: 'Assisted Living Facility', value: 'assisted_living' },
        { label: 'Rehabilitation Center', value: 'rehabilitation_center' },
        { label: 'Memory Care Unit', value: 'memory_care' },
        { label: 'Hospice / End-of-Life Care', value: 'hospice' },
        { label: 'Private Home', value: 'private_home' },
        { label: 'Outpatient Clinic', value: 'clinic' },
        { label: 'Any', value: 'any_environment_setting' },
      ],
      roomTypeOptions: [
        { label: 'Patient Room', value: 'patient_room' },
        { label: 'Shared Patient Room', value: 'shared_room' },
        { label: 'Private Patient Room', value: 'private_room' },
        { label: 'Bathroom', value: 'bathroom' },
        { label: 'Dining Room', value: 'dining_room' },
        { label: 'Hallway', value: 'hallway' },
        { label: "Nurse's Station", value: 'nurse_station' },
        { label: 'Treatment Room', value: 'treatment_room' },
        { label: 'Recovery Room', value: 'recovery_room' },
        { label: 'Exam Room', value: 'exam_room' },
        { label: 'Triage Room', value: 'triage_room' },
        { label: 'Isolation Room', value: 'isolation_room' },
        { label: 'Home Bedroom', value: 'home_bedroom' },
        { label: 'Home Bathroom', value: 'home_bathroom' },
        { label: 'Home Living Room', value: 'living_room' },
        { label: 'Kitchen', value: 'kitchen' },
        { label: 'Any', value: 'any_room_type' },
      ],
      timeOfDayOptions: [
        { label: 'Morning (6AM–12PM)', value: 'morning' },
        { label: 'Afternoon (12PM–4PM)', value: 'afternoon' },
        { label: 'Evening (4PM–8PM)', value: 'evening' },
        { label: 'Night (8PM–12AM)', value: 'night' },
        { label: 'Overnight (12AM–6AM)', value: 'overnight' },
        { label: 'Any', value: 'any_time_of_day' },
      ],
      sceneMoodOptions: [
        { label: 'Calm', value: 'calm' },
        { label: 'Busy', value: 'busy' },
        { label: 'Quiet', value: 'quiet' },
        { label: 'Tense', value: 'tense' },
        { label: 'Urgent', value: 'urgent' },
        { label: 'Chaotic', value: 'chaotic' },
        { label: 'Welcoming', value: 'welcoming' },
        { label: 'Somber', value: 'somber' },
        { label: 'Any', value: 'any_scene_mood' },
      ],
      windowWidth: window.innerWidth,
    };
  },
  watch: {
    // skillmap: {
    //   handler(newValue) {
    //     this.$emit('update:formData', newValue);
    //   },
    //   deep: true,
    // },
    'skillmap.id': {
      handler(newVal) {
        if (newVal) {
          this.fetchSkillmapSteps();
        }
      },
    },
    // formData: {
    //   handler(newValue) {
    //     this.skillmap = JSON.parse(JSON.stringify(newValue));
    //   },
    //   deep: true,
    // },
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },

  methods: {
    ...mapActions('aiSkills', [
      'getAllSkillMaps',
      'getSkillMapSteps',
      'getSkillCardImage',
      'setSkillCard',
      'getSkillCardPrompt',
      'setSkillCardValues',
      'updateRequirePatient',
    ]),
    async goToNextTab() {
      //   this.$parent.activeTab = 'anchor';
      if (!this.skillmap.id || !this.skillmap.skillStep) {
        this.skillmapRequired = !this.skillmap.id;
        this.skillstepRequired = !this.skillmap.skillStep;
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      } else {
        this.skillmapRequired = false;
        this.skillstepRequired = false;
        this.onNext(1);
      }
    },
    async fetchSchoolSkillmaps() {
      this.isLoadingSkillmaps = true;
      const resp = await this.getAllSkillMaps();
      this.skillMaps = resp.data ? resp.data : [];
      this.schoolSkillMaps = resp.data
        ? resp.data.map(skillmap => {
            return { id: skillmap.id, label: skillmap.title };
          })
        : [];
      this.isLoadingSkillmaps = false;
    },
    async fetchSkillmapSteps() {
      this.isLoadingSkillSteps = true;
      if (this.skillmap.id) {
        const resp = await this.getSkillMapSteps({ id: this.skillmap.id });
        this.skillStepOptions = resp.data
          ? resp.data.map(step => {
              return { id: step.id, label: step.step };
            })
          : [];
      }
      this.isLoadingSkillSteps = false;
    },
    onSkillCardChange(name = '') {
      if (name == 'skillmap') {
        const skill = this.skillMaps.filter(skill => skill.id == this.skillmap.id);
        this.requirePatient = skill.length ? skill[0].requires_patient : true;
        this.updateRequirePatient(this.requirePatient);
      }
      const step_name = this.skillStepOptions.filter(skillstep => skillstep.id === this.skillmap.skillStep);
      const data = {
        step: this.skillmap.skillStep,
        step_name: step_name.length ? step_name[0].label : '',
        skill_map: this.skillmap.id,
        actor_race: this.skillmap.race,
        actor_gender: this.skillmap.gender,
        actor_age: this.skillmap.actorAge,
        uniform_type: this.skillmap.uniformType,
        uniform_color: this.skillmap.uniformColor,
        ...(this.requirePatient
          ? {
              patient_age: this.skillmap.patientAge,
              patient_condition: this.skillmap.patientCondition,
              patient_position: this.skillmap.patientPosition,
              patient_race: this.skillmap.patientRace,
              patient_gender: this.skillmap.patientGender,
            }
          : {}),
        environment_setting: this.skillmap.envSetting,
        room_type: this.skillmap.roomType,
        time_of_day: this.skillmap.timeDay,
        scene_mood: this.skillmap.sceneMood,
      };
      this.setSkillCardValues({ ...data });
    },
  },
  mounted() {
    this.fetchSchoolSkillmaps();
  },
};
</script>
<style lang="css" scoped>
.scenario-info {
  background-color: rgb(239 246 255 / var(--tw-bg-opacity, 1));
  border-radius: 8px;
  font-size: medium;
}

.scenario-section {
  margin-bottom: 2rem;
}

.section-header h5 {
  margin: 0;
  color: #374151;
  font-weight: 600;
}

.form-section-title {
  font-size: 15px;
  padding-top: 16px;
}

/* Card styling */
.bg-white.rounded-lg {
  transition: all 0.2s ease-in-out;
}

.bg-white.rounded-lg:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

/* Form improvements */
.form-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.v-select-custom {
  border-radius: 0.375rem;
}
</style>
